import React, { useMemo, useState } from "react";
import { Button, Col, Container, Dropdown, Form, Modal, Row } from "react-bootstrap";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { MIME_TYPES } from "config/constants";
import { CommonToolTip, CstmPagination, RequiredFieldAstrick } from "components/Common/Common";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import AppInputField from "components/shared/FormComponents/AppInputField";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingButton from "components/shared/LoadingButton";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import TableLayoutV2 from "components/TableLayoutV2";
import { successMessageToast } from "helpers/common";
import { useTherapistLookupQuery } from "store/services/utility";
import {
  useCreatePolicyMutation,
  useDeletePolicyMutation,
  useGetPoliciesListQuery,
  useLazyGetPolicyDetailsQuery,
  useUpdatePolicyMutation,
} from "store/services/policies";
import { POLICY_AND_PROCEDURE_V2_ADD, POLICY_AND_PROCEDURE_V2_DETAIL, POLICY_AND_PROCEDURE_V2_EDIT } from "config/paths";

const emptyPolicy = { name: "", attachment_key: "", attachment_name: "", audience_type: "global", user_ids: [], therapist_search: "" };

const policySchema = Yup.object({
  name: Yup.string().trim().required("Policy name is required"),
  attachment_key: Yup.string().required("An attachment is required"),
  audience_type: Yup.string().oneOf(["global", "selected"]).required(),
  user_ids: Yup.array().when("audience_type", {
    is: "selected",
    then: (schema) => schema.min(1, "Select at least one therapist"),
  }),
});

const getAttachmentName = (key = "") => key.split("/").filter(Boolean).pop() || "";

const policyValues = (policy = {}) => ({
  ...emptyPolicy,
  name: policy?.name || policy?.title || "",
  attachment_key: policy?.attachment_key || policy?.attachment?.attachment_key || "",
  attachment_name: policy?.attachment_name || policy?.attachment?.attachment_name || "",
  audience_type: policy?.audience_type || "global",
  user_ids: (policy?.user_ids || policy?.users?.map((user) => user.id) || []).map(Number),
});

const PoliciesAndProceduresV2 = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ column: "id", direction: "desc" });
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const pageSize = 10;

  const { data, isLoading, isError, isFetching } = useGetPoliciesListQuery({ page: currentPage, limit: pageSize, search, sort });
  const { data: therapists = [], isLoading: therapistsLoading } = useTherapistLookupQuery();
  const [getPolicyDetails] = useLazyGetPolicyDetailsQuery();
  const [createPolicy, { isLoading: isCreating }] = useCreatePolicyMutation();
  const [updatePolicy, { isLoading: isUpdating }] = useUpdatePolicyMutation();
  const [deletePolicy, { isLoading: isDeleting }] = useDeletePolicyMutation();

  const policies = data?.records || data?.policies || data?.data || [];
  const totalRecords = data?.totalRecords || data?.total_records || data?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const openEdit = async (policy) => {
    setEditingPolicy(policy);
    setShowForm(true);
    try {
      const details = await getPolicyDetails(policy.id).unwrap();
      setEditingPolicy(details?.policy || details);
    } catch (error) {
      console.error("Unable to load policy details", error);
    }
  };

  const submitPolicy = async (values) => {
    const payload = {
      name: values.name.trim(),
      attachment_key: values.attachment_key,
      attachment_name: values.attachment_name || getAttachmentName(values.attachment_key),
      audience_type: values.audience_type,
      user_ids: values.audience_type === "global" ? [] : values.user_ids,
    };
    try {
      const result = editingPolicy?.id
        ? await updatePolicy({ id: editingPolicy.id, data: payload }).unwrap()
        : await createPolicy(payload).unwrap();
      successMessageToast(result?.message || `Policy ${editingPolicy?.id ? "updated and resent" : "created"} successfully.`);
      setShowForm(false);
      setEditingPolicy(null);
    } catch (error) {
      console.error("Unable to save policy", error);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      const result = await deletePolicy(deleteTarget.id).unwrap();
      successMessageToast(result?.message || "Policy deleted successfully.");
      setDeleteTarget(null);
    } catch (error) {
      console.error("Unable to delete policy", error);
    }
  };

  const columns = useMemo(() => [
    {
      head: "POLICY NAME",
      accessor: "name",
      sorter: true,
      component: (item) => (
        <button
          type="button"
          className="p-0 border-0 bg-transparent text-capitalize cursor-pointer d-flex align-items-center gap-2"
        >
          {item?.name || "--"}
        </button>
      )

    },
    {
      head: "Description",
      accessor: "description",
      sorter: true,
      component: (item) => <h6 className="m-0 text-capitalize cursor-pointer d-flex align-items-center gap-2">{<CommonToolTip component={<p>{ item?.description ? `${item?.description?.slice(0,100)}${item?.description?.slice(100) ? '...' : ''}` : "--"}</p>} text={item?.description || "--"} />}</h6>

    },
    {
      head: "ATTACHMENT",
      accessor: "attachment_name",
      component: (item) => <h6 className="m-0 text-capitalize cursor-pointer d-flex align-items-center gap-2">{item?.attachment_name || item?.attachment?.attachment_name || "--"}</h6>
    },
    {
      head: "AUDIENCE",
      accessor: "audience_type",
      sorter: true, component: (item) => <h6 className="m-0 text-capitalize cursor-pointer d-flex align-items-center gap-2">{item?.audience_type || "--"}</h6>
    },
    {
      head: "",
      accessor: "actions",
      isComponent: true,
      component: (item) =>
        //  <div className="d-flex gap-2" onClick={(event) => event.stopPropagation()}>
        //   <Button variant="transparent" className="p-0 themeBlue fw-sbold" onClick={() => openEdit(item)}>Edit & Resend</Button>
        //   <Button variant="transparent" className="p-0 text-danger fw-sbold" onClick={() => setDeleteTarget(item)}>Delete</Button>
        // </div>
        <div className="ActnBtn d-flex align-items-center  gap-10">
          <Dropdown
            onClick={(e) => e.stopPropagation()}
          // show={index == dropdownIndex}
          // onToggle={() => setDropdownIndex(index == dropdownIndex ? null : index)}
          >
            <Dropdown.Toggle
              variant="transparent"
              className="p-0 border-0 text-dark w-100"
              id="dropdown-basic"
            >
              <span className="icn ">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="13"
                  viewBox="0 0 28 13"
                  fill="none"
                >
                  <rect width="28" height="13" rx="6.5" fill="" />
                  <path
                    d="M23.2678 4.73223C22.2915 3.75592 20.7085 3.75592 19.7322 4.73223C18.7559 5.70854 18.7559 7.29146 19.7322 8.26777C20.7085 9.24408 22.2915 9.24408 23.2678 8.26777C24.2441 7.2915 24.2441 5.70858 23.2678 4.73223Z"
                    fill="#8f8f8f"
                  />
                  <path
                    d="M16.2678 4.73223C15.2915 3.75592 13.7085 3.75592 12.7322 4.73223C11.7559 5.70854 11.7559 7.29146 12.7322 8.26777C13.7085 9.24408 15.2915 9.24408 16.2678 8.26777C17.2441 7.2915 17.2441 5.70858 16.2678 4.73223"
                    fill="#8f8f8f"
                  />
                  <path
                    d="M9.26777 4.73223C8.29146 3.75592 6.70854 3.75592 5.73223 4.73223C4.75592 5.70854 4.75592 7.29146 5.73223 8.26777C6.70854 9.24408 8.29146 9.24408 9.26777 8.26777C10.2441 7.2915 10.2441 5.70858 9.26777 4.73223"
                    fill="#8f8f8f"
                  />
                </svg>
              </span>
            </Dropdown.Toggle>

            <Dropdown.Menu className="" style={{ right: 0, left: "unset" }}>
              <ul className="list-unstyled mb-0" style={{ paddingLeft: "19px" }}>
                <h5 onClick={(e) => { e.stopPropagation(); navigate(generatePath(POLICY_AND_PROCEDURE_V2_EDIT, { id: item.id })) }} className="cursor-pointer proxima mb-2">
                  Edit
                </h5>
                <h5 onClick={(e) => { e.stopPropagation(); setDeleteTarget(item) }} className="cursor-pointer proxima mb-2">
                  Delete
                </h5>
              </ul>
            </Dropdown.Menu>
          </Dropdown>

        </div >

      ,
    },
  ], []);

  return <>
    <ConfirmationPop confirmation={Boolean(deleteTarget)} setConfirmation={() => setDeleteTarget(null)} confirmHandler={confirmDelete} type="delete" />
    <PolicyFormModal
      show={showForm}
      onHide={() => { setShowForm(false); setEditingPolicy(null); }}
      initialValues={policyValues(editingPolicy)}
      therapists={therapists}
      therapistsLoading={therapistsLoading}
      isSaving={isCreating || isUpdating}
      isEditing={Boolean(editingPolicy?.id)}
      onSubmit={submitPolicy}
    />
    <section className="ManageProfile position-relative py-4">
      <Container fluid>
        <Row>
          <Col lg="12" className="my-2">
            <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
              <div className="clinican-head">
                <div className="left classroom-conslutation"><h6 className="m-0 fw-bold main-head themeBlue">Policies & Procedures</h6></div>
              </div>
              <Link to={POLICY_AND_PROCEDURE_V2_ADD} className="blue-discharge-screen-btn mt-0 d-flex align-items-center justify-content-center">
                Add Policy
              </Link>
              {/* <Button className="blue-discharge-screen-btn-new mt-0" onClick={() => navigate(POLICY_AND_PROCEDURE_V2_ADD)}>Add Policy</Button> */}
            </div>
          </Col>
          <Row style={{ paddingRight: "0" }} className="reports-v2 my-3 d-flex justify-content-end classroom-conslutation">
            {/* <Col lg="3" className="">
              <div className="d-flex gap-3 mt-2">
                <input className="input-new readonly-box h-auto search-input-filter-v2" placeholder="Find a policy..." value={search} />
              </div>
            </Col> */}
            <Col style={{ paddingRight: "0" }} lg="3">
              <div className="w-100 d-flex align-items-center gap-3">
                <label className="discharge-screen-label mb-0">Search</label>
                <input
                  onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }}                  // style={{outline: "blue"}}
                  className={`input-new readonly-box h-auto search-input-filter-v2`}
                  placeholder="Find a client..."
                  value={search}
                />
              </div>
            </Col>
          </Row>
          <Col lg="12" className="my-2">
            <div className="table-outer">
              {isLoading ? <div style={{ height: "200px" }}><LoadingIndicator /></div> : isError ? <ErrorComponent /> : <>
                {(isFetching || isDeleting) && <LoadingOverlay />}
                <TableLayoutV2 data={policies} column={columns} filter={{ sort }} url={POLICY_AND_PROCEDURE_V2_DETAIL?.replace("/:id", "")} handleSort={(nextSort) => { setSort(nextSort); setCurrentPage(1); }} />
              </>}
              <CstmPagination totalPages={totalPages} activePage={currentPage} className="paginationV2" handlePageChange={(page) => { window.scrollTo(0, 0); setCurrentPage(page); }} />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  </>;
};

const PolicyFormModal = ({ show, onHide, initialValues, therapists, therapistsLoading, isSaving, isEditing, onSubmit }) => (
  <Modal show={show} onHide={onHide} size="lg" centered>
    <Formik initialValues={initialValues} validationSchema={policySchema} onSubmit={onSubmit} enableReinitialize>
      {({ handleSubmit, values, setFieldValue }) => {
        const filteredTherapists = therapists.filter((therapist) => `${therapist?.firstname || ""} ${therapist?.lastname || ""} ${therapist?.email || ""}`.toLowerCase().includes(values.therapist_search.toLowerCase()));
        const toggleTherapist = (id) => setFieldValue("user_ids", values.user_ids.includes(id) ? values.user_ids.filter((userId) => userId !== id) : [...values.user_ids, id]);
        return <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton><Modal.Title>{isEditing ? "Update & Resend Policy" : "Create Policy"}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Row>
              <Col md="12" className="mb-3">
                <label className="form-label themeBlue fw-bold d-flex">Policy name<RequiredFieldAstrick /></label>
                <AppInputField name="name" type="text" className="form-control" placeholder="Enter policy name" />
              </Col>
              <Col md="12" className="mb-3">
                <label className="form-label themeBlue fw-bold d-flex">Attachment<RequiredFieldAstrick /></label>
                <AppFileUploadField name="attachment_key" acl="private" accept="application/pdf" allowedTypes={[MIME_TYPES.pdf]} onChange={(_name, key) => { setFieldValue("attachment_key", key); setFieldValue("attachment_name", getAttachmentName(key)); }} />
                {values.attachment_name && <small className="text-muted">{values.attachment_name}</small>}
              </Col>
              <Col md="12" className="mb-2">
                <label className="form-label themeBlue fw-bold d-flex">Audience<RequiredFieldAstrick /></label>
                <div className="d-flex gap-4">
                  {["global", "selected"].map((type) => <Form.Check key={type} inline type="radio" id={`audience-${type}`} label={type === "global" ? "Global" : "Selected therapists"} checked={values.audience_type === type} onChange={() => { setFieldValue("audience_type", type); if (type === "global") setFieldValue("user_ids", []); }} />)}
                </div>
              </Col>
              {values.audience_type === "selected" && <Col md="12">
                <label className="form-label themeBlue fw-bold">Select therapists<RequiredFieldAstrick /></label>
                <AppInputField name="therapist_search" type="text" className="form-control mb-2" placeholder="Search therapists..." />
                <div className="table-responsive border rounded" style={{ maxHeight: 260 }}>
                  {therapistsLoading ? <LoadingIndicator /> : <table className="table mb-0"><thead><tr><th></th><th>Name</th><th>Email</th></tr></thead><tbody>{filteredTherapists.length ? filteredTherapists.map((therapist) => <tr key={therapist.id}><td><Form.Check aria-label={`Select ${therapist.firstname}`} checked={values.user_ids.includes(Number(therapist.id))} onChange={() => toggleTherapist(Number(therapist.id))} /></td><td>{therapist.firstname} {therapist.lastname}</td><td>{therapist.email || "--"}</td></tr>) : <tr><td colSpan="3" className="text-center">No therapists found</td></tr>}</tbody></table>}
                </div>
                {values.user_ids.length === 0 && <div className="error">Select at least one therapist</div>}
              </Col>}
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="light" onClick={onHide}>Cancel</Button><LoadingButton type="submit" isLoading={isSaving} className="commonBtn">{isEditing ? "Update & Resend" : "Create Policy"}</LoadingButton></Modal.Footer>
        </Form>;
      }}
    </Formik>
  </Modal>
);

export default PoliciesAndProceduresV2;
